import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, UserX, Building2, Inbox, Trophy, ScrollText, ArrowRight, Tag, ClipboardList, Webhook } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty } from "../../components/UI";
import { toast } from "sonner";
import { CinematicReprocessButton } from "./CinematicReprocess";
import { CertificateSignatureCard } from "./CertificateSignature";

const STATS = [
  { key: "total_users", label: "Total Users", icon: Users, to: "/admin/users?status=all" },
  { key: "active_users", label: "Active Users", icon: UserCheck, to: "/admin/users?status=active" },
  { key: "deactivated_users", label: "Deactivated", icon: UserX, to: "/admin/users?status=deactivated" },
  { key: "total_clubs", label: "Total Clubs", icon: Building2, to: "/clubs", hint: "registry" },
  { key: "pending_club_requests", label: "Pending Requests", icon: Inbox, to: "/admin/club-requests" },
  { key: "pending_name_changes", label: "Name Changes", icon: Tag, to: "/admin/name-change-requests" },
  { key: "pending_entry_requests", label: "Entry Requests", icon: ClipboardList, to: "/admin/entry-requests" },
  { key: "admin_competitions", label: "Admin Competitions", icon: Trophy, to: "/admin/competitions" },
  { key: "total_partners", label: "Total Partners", icon: Webhook, to: "/admin/partners" },
  { key: "active_partners", label: "Active Partners", icon: Webhook, to: "/admin/partners" },
];

function StatCard({ label, value, Icon, delay, to, hint }) {
  const slug = label.toLowerCase().replace(/\s+/g, "-");
  return (
    <Link
      to={to}
      aria-label={`${label}${typeof value === "number" ? ` (${value})` : ""} — open ${hint || "section"}`}
      className="group block rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] p-5 animate-fade-up cursor-pointer hover:border-[#E5B842]/60 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B842]/60 focus-visible:border-[#E5B842]/60"
      style={{ animationDelay: `${delay}ms`, transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      data-testid={`admin-stat-${slug}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#E5B842]" />}
      </div>
      <div className="font-heading text-4xl font-black text-slate-50 mt-3 tabular-nums" data-testid={`admin-stat-value-${slug}`}>
        {value ?? "—"}
      </div>
      <div className="mt-3 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }}>
        {hint || "View"} <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ov, al] = await Promise.all([
          api.get("/admin/overview"),
          api.get("/admin/audit-log", { params: { page: 1, page_size: 8 } }),
        ]);
        setOverview(ov.data);
        setAudit(al.data.entries || []);
      } catch (e) {
        toast.error(apiErr(e, "Failed to load admin dashboard"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Control Center</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none">Dashboard</h1>
            <p className="text-slate-400 mt-2 text-sm sm:text-base">Platform health at a glance.</p>
          </div>
          <CinematicReprocessButton />
        </div>

        {loading ? (
          <Loader label="Loading dashboard…" />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats-grid">
              {STATS.map((s, i) => (
                <StatCard key={s.key} label={s.label} value={overview?.[s.key]} Icon={s.icon} delay={i * 60} to={s.to} hint={s.hint} />
              ))}
            </div>

            <div className="mt-10" data-testid="admin-audit-preview">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ScrollText className="w-4 h-4 text-[#E5B842]" />
                  <h2 className="font-heading text-lg font-black uppercase tracking-tight text-slate-100">Recent Admin Activity</h2>
                </div>
                <Link to="/admin/audit-log" data-testid="admin-view-full-audit" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                  View all <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              {audit.length === 0 ? (
                <Empty label="No admin actions recorded yet" />
              ) : (
                <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] divide-y divide-[rgba(212,175,55,0.10)]" data-testid="admin-audit-list-preview">
                  {audit.map((a) => (
                    <AuditRow key={a.id} a={a} />
                  ))}
                </div>
              )}
            </div>
            <CertificateSignatureCard />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export function AuditRow({ a }) {
  const when = a.created_at ? new Date(a.created_at).toLocaleString() : "—";
  return (
    <div className="flex items-start gap-3 p-4" data-testid={`audit-row-${a.id}`}>
      <div className="w-2 h-2 rounded-full bg-[#E5B842] mt-1.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-heading text-sm font-bold uppercase tracking-wide text-slate-100 break-words">{a.action}</span>
          {a.target_type && (
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1a1407] text-[#E5B842] break-words">{a.target_type}</span>
          )}
        </div>
        <div className="text-xs text-slate-500 font-mono mt-1 break-words">{a.actor_email || "system"}</div>
        {a.payload_summary && (
          <div className="text-xs text-slate-400 mt-1 break-words">{typeof a.payload_summary === "string" ? a.payload_summary : JSON.stringify(a.payload_summary)}</div>
        )}
      </div>
      <div className="text-[10px] text-slate-600 font-mono whitespace-nowrap shrink-0">{when}</div>
    </div>
  );
}
